<template lang="pug">
b-modal(
  id="job-log-modal",
  ref="jobLogModal",
  size="xl",
  :title="modalTitle",
  hide-footer,
  @hidden="onHidden",
  @shown="onShown"
)
  template(#modal-header)
    .d-flex.justify-content-between.align-items-center.w-100
      .d-flex.align-items-center
        i.fa.fa-terminal.mr-2.text-muted
        h5.mb-0 {{ modalTitle }}
      .d-flex.align-items-center
        b-badge.mr-3(:variant="statusVariant") {{ statusLabel }}
        span.small.text-muted.mr-3(v-if="job && job.progress && job.total_documents")
          | {{ job.progress }} / {{ job.total_documents }}
        b-button(
          variant="outline-secondary",
          size="sm",
          @click="fetchLog",
          :disabled="loading",
          v-b-tooltip.hover,
          title="Log aktualisieren"
        )
          i.fa.fa-refresh(:class="{ 'fa-spin': loading }")
        b-button(
          variant="outline-secondary",
          size="sm",
          @click="toggleAutoScroll",
          :pressed="autoScroll",
          v-b-tooltip.hover,
          :title="autoScroll ? 'Auto-Scroll deaktivieren' : 'Auto-Scroll aktivieren'"
        ).ml-2
          i.fa(:class="autoScroll ? 'fa-lock' : 'fa-unlock'")
        b-button(
          variant="link",
          size="sm",
          @click="$refs.jobLogModal.hide()"
        ).ml-2
          i.fa.fa-times

  // Job Info Bar
  .job-info-bar.mb-3(v-if="job")
    b-row.align-items-center
      b-col(cols="auto")
        small.text-muted Typ: 
        b-badge(variant="secondary") {{ actionLabel }}
      b-col(cols="auto")
        small.text-muted Projekt: 
        span {{ job.project_name || '-' }}
      b-col(cols="auto")
        small.text-muted Erstellt: 
        span {{ formatDate(job.created_at) }}
      b-col(cols="auto", v-if="job.owner")
        small.text-muted Von: 
        span {{ job.owner.email }}
      b-col(cols="auto")
        small.text-muted(:class="{ 'text-success': isPolling }")
          i.fa.fa-circle.mr-1(:class="isPolling ? 'text-success' : 'text-muted'")
          | {{ isPolling ? 'Live-Update aktiv' : 'Polling gestoppt' }}
      b-col.ml-auto
        small.text-muted.mr-2 Filter:
        b-button-group(size="sm")
          b-button(
            :variant="isFilterActive('ALL') ? 'primary' : 'outline-secondary'",
            @click="setFilter('ALL')"
          ) Alle
          b-button(
            :variant="isFilterActive('INFO') ? 'info' : 'outline-secondary'",
            @click="toggleFilter('INFO')"
          ) Info
          b-button(
            :variant="isFilterActive('WARNING') ? 'warning' : 'outline-secondary'",
            @click="toggleFilter('WARNING')"
          ) Warning
          b-button(
            :variant="isFilterActive('ERROR') ? 'danger' : 'outline-secondary'",
            @click="toggleFilter('ERROR')"
          ) Error
          b-button(
            :variant="isFilterActive('DEBUG') ? 'secondary' : 'outline-secondary'",
            @click="toggleFilter('DEBUG')"
          ) Debug
        small.text-muted.ml-2(v-if="filteredLineCount !== totalLineCount")
          | {{ filteredLineCount }} / {{ totalLineCount }} Zeilen

  // Progress Bar
  b-progress.mb-3(
    v-if="job && job.total_documents",
    :value="job.progress",
    :max="job.total_documents",
    :variant="progressVariant",
    height="6px",
    :animated="job.status === 'IN_PROGRESS'"
  )

  // Console Output
  .console-output(ref="consoleOutput")
    .log-content(v-if="logContent")
      .log-line(
        v-for="(line, index) in filteredLogLines",
        :key="index",
        :class="getLogLineClass(line)"
      ) {{ line }}
    .no-log(v-else-if="!loading")
      i.fa.fa-info-circle.mr-2
      | Noch keine Log-Einträge vorhanden
    .loading-log(v-else)
      b-spinner(small).mr-2
      | Lade Log...

</template>

<script>
export default {
  name: 'JobLogModal',
  data() {
    return {
      job: null,
      logContent: '',
      loading: false,
      autoScroll: true,
      pollInterval: null,
      isPolling: false,
      activeFilters: ['ALL']
    }
  },
  computed: {
    modalTitle() {
      if (this.job) {
        return `Job Log: ${this.job.project_name || this.job._id}`
      }
      return 'Job Log'
    },
    statusVariant() {
      if (!this.job) return 'secondary'
      switch (this.job.status) {
        case 'DONE':
          return 'success'
        case 'IN_PROGRESS':
          return 'info'
        case 'DELETED':
          return 'danger'
        default:
          return 'secondary'
      }
    },
    statusLabel() {
      if (!this.job) return ''
      switch (this.job.status) {
        case 'DONE':
          return 'Fertig'
        case 'IN_PROGRESS':
          return 'In Bearbeitung'
        case 'DELETED':
          return 'Abgebrochen'
        default:
          return this.job.status
      }
    },
    actionLabel() {
      if (!this.job) return ''
      switch (this.job.action) {
        case 'IMPORT':
          return 'Import'
        case 'EXPORT':
          return 'Export'
        case 'EXTRACT':
          return 'Extraktion'
        default:
          return this.job.action
      }
    },
    progressVariant() {
      if (!this.job) return 'info'
      if (this.job.status === 'DONE') return 'success'
      if (this.job.status === 'DELETED') return 'danger'
      return 'info'
    },
    logLines() {
      if (!this.logContent) return []
      return this.logContent.split('\n')
    },
    totalLineCount() {
      return this.logLines.length
    },
    filteredLogLines() {
      if (this.activeFilters.includes('ALL')) {
        return this.logLines
      }
      return this.logLines.filter(line => {
        if (!line.trim()) return true
        return this.activeFilters.some(filter => {
          const pattern = new RegExp(`\\|\\s*${filter}\\s*\\|`, 'i')
          return pattern.test(line)
        })
      })
    },
    filteredLineCount() {
      return this.filteredLogLines.length
    }
  },
  methods: {
    open(job) {
      this.job = job
      this.logContent = ''
      this.activeFilters = ['ALL']
      this.$refs.jobLogModal.show()
    },
    isFilterActive(filter) {
      return this.activeFilters.includes(filter)
    },
    setFilter(filter) {
      if (filter === 'ALL') {
        this.activeFilters = ['ALL']
      }
    },
    toggleFilter(filter) {
      const idx = this.activeFilters.indexOf(filter)
      
      if (this.activeFilters.includes('ALL')) {
        this.activeFilters = [filter]
      } else if (idx > -1) {
        this.activeFilters.splice(idx, 1)
        if (this.activeFilters.length === 0) {
          this.activeFilters = ['ALL']
        }
      } else {
        this.activeFilters.push(filter)
      }
    },
    getLogLineClass(line) {
      if (!line) return ''
      if (/\|\s*ERROR\s*\|/i.test(line)) return 'log-error'
      if (/\|\s*WARNING\s*\|/i.test(line)) return 'log-warning'
      if (/\|\s*INFO\s*\|/i.test(line)) return 'log-info'
      if (/\|\s*DEBUG\s*\|/i.test(line)) return 'log-debug'
      return ''
    },
    onShown() {
      this.fetchLog()
      this.startPolling()
    },
    onHidden() {
      this.stopPolling()
      this.job = null
      this.logContent = ''
    },
    async fetchLog() {
      if (!this.job) return
      
      this.loading = true
      try {
        const response = await this.axios.get(
          this.base_url + '/jobs/' + this.job._id + '/log'
        )
        this.logContent = response.data
        
        // Also refresh job status
        const jobResponse = await this.axios.get(
          this.base_url + '/jobs/' + this.job._id
        )
        this.job = jobResponse.data
        
        if (this.autoScroll) {
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }
        
        // Stop polling if job is done
        if (this.job.status === 'DONE' || this.job.status === 'DELETED') {
          this.stopPolling()
        }
      } catch (error) {
        console.error('Error fetching log:', error)
        if (error.response && error.response.status === 404) {
          this.logContent = 'Job nicht gefunden.'
        } else {
          this.logContent = 'Fehler beim Laden des Logs: ' + error.message
        }
      } finally {
        this.loading = false
      }
    },
    startPolling() {
      this.stopPolling() // Clear any existing interval
      this.isPolling = true
      this.pollInterval = setInterval(() => {
        this.fetchLog()
      }, 5000) // Poll every 5 seconds
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
      this.isPolling = false
    },
    toggleAutoScroll() {
      this.autoScroll = !this.autoScroll
      if (this.autoScroll) {
        this.scrollToBottom()
      }
    },
    scrollToBottom() {
      const consoleEl = this.$refs.consoleOutput
      if (consoleEl) {
        consoleEl.scrollTop = consoleEl.scrollHeight
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  },
  beforeDestroy() {
    this.stopPolling()
  }
}
</script>

<style scoped>
.console-output {
  background-color: #1e1e1e;
  border-radius: 6px;
  padding: 16px;
  min-height: 400px;
  max-height: 70vh;
  overflow-y: auto;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;
}

.log-content {
  color: #d4d4d4;
  margin: 0;
  background: transparent;
  border: none;
  font-family: inherit;
  font-size: inherit;
}

.log-line {
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 1px 0;
}

.log-line.log-error {
  color: #f44747;
}

.log-line.log-warning {
  color: #dcdcaa;
}

.log-line.log-info {
  color: #4ec9b0;
}

.log-line.log-debug {
  color: #808080;
}

.no-log, .loading-log {
  color: #808080;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.job-info-bar {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 13px;
}

/* Scrollbar styling for the console */
.console-output::-webkit-scrollbar {
  width: 8px;
}

.console-output::-webkit-scrollbar-track {
  background: #2d2d2d;
  border-radius: 4px;
}

.console-output::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 4px;
}

.console-output::-webkit-scrollbar-thumb:hover {
  background: #666;
}

</style>

<style>
/* Global modal styles */
#job-log-modal .modal-dialog {
  max-width: 90%;
}

#job-log-modal .modal-content {
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

#job-log-modal .modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 12px 16px;
}

#job-log-modal .modal-body {
  padding: 16px;
}
</style>
